import { CircleAlert, CircleCheck, CircleHelp } from "lucide-react";
import { Spinner, Tooltip } from "@marekl/adminer/components";
import { useVatPayerStatus } from "../../lib/queries/registry";
import styles from "./VatStatusBadge.module.css";

type State = "ok" | "bad" | "unknown";

const ICONS = { ok: CircleCheck, bad: CircleAlert, unknown: CircleHelp } as const;

/**
 * The DIČ's entry in the MFČR VAT payer register, as the legacy field's
 * addon showed it (legacy/app/src/utils/Inputs.tsx VATPayerInfoAdornment):
 * "DPH" with a green tick for a reliable payer, a red mark for an
 * unreliable one — whoever pays its VAT invoice is liable for the VAT
 * (zákon o DPH § 109) — and a question mark when it is not known. The
 * tooltip says which.
 */
export function VatStatusBadge({ dic }: { dic: string | undefined }) {
  const status = useVatPayerStatus(dic);
  if (dic && status.isLoading) return <Spinner size="sm" aria-label="Ověřuji DIČ v registru plátců DPH" />;

  let state: State = "unknown";
  let tip = "Zadejte DIČ; ověří se v registru plátců DPH.";
  if (dic && status.error) tip = "Registr plátců DPH teď neodpovídá.";
  else if (dic && status.data) {
    const s = status.data;
    if (s.unreliable) {
      state = "bad";
      tip = "Nespolehlivý plátce: za DPH z jeho faktury ručí ten, kdo ji zaplatí.";
    } else if (!s.registered) {
      tip = "Není v registru plátců DPH.";
    } else {
      state = "ok";
      tip = `Spolehlivý plátce DPH. Zveřejněné účty: ${s.published_accounts.length || "žádné"}`;
    }
  }
  const Icon = ICONS[state];
  return (
    <Tooltip content={tip}>
      <span className={styles.vat} data-state={state} tabIndex={0} aria-label={tip}>
        DPH <Icon size={16} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}
